import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  BookOpen, Plus, PenSquare, Trash2, ChevronDown, ChevronUp, 
  PlayCircle, Clock, Check, X, Layers, Sparkles, AlertCircle,
  Eye, EyeOff, HardDrive, Video, ExternalLink, ShieldCheck, CheckCircle2,
  FileText, Upload, Paperclip, Lock, Link2, FileCheck
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getVideoEmbedInfo } from "@/lib/videoEmbed";

export default function CourseBuilder({ courseId, onBack }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  // Inline editing state for modules
  const [editingModuleId, setEditingModuleId] = useState(null); // module_id or 'new'
  const [moduleForm, setModuleForm] = useState({ title: "", description: "" });
  const [savingModule, setSavingModule] = useState(false);

  // Inline editing state for lessons
  const [editingLessonId, setEditingLessonId] = useState(null); // lesson_id
  const [addingLessonModuleId, setAddingLessonModuleId] = useState(null); // module_id
  const [lessonForm, setLessonForm] = useState({
    title: "",
    description: "",
    video_url: "",
    duration_minutes: "",
    notes_url: "",
    notes_title: "",
    notes_file_id: "",
    notes_type: "upload"
  });
  const [notesSourceTab, setNotesSourceTab] = useState("upload"); // 'upload' | 'link'
  const [uploadingNotes, setUploadingNotes] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  // Module collapse state
  const [collapsedModules, setCollapsedModules] = useState({});

  const toggleCollapse = (moduleId) => {
    setCollapsedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const fetchCourseTree = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/courses/${courseId}/tree`, { withCredentials: true });
      setCourse(res.data);
    } catch (err) {
      console.error("Tree fetch error:", err.response || err);
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchCourseTree();
  }, [fetchCourseTree]);

  // Start editing a module
  const startEditModule = (mod) => {
    setEditingModuleId(mod.module_id);
    setModuleForm({ title: mod.title || "", description: mod.description || "" });
    setCollapsedModules(prev => ({ ...prev, [mod.module_id]: false }));
  };

  // Start adding a new module
  const startAddModule = () => {
    setEditingModuleId("new");
    setModuleForm({ title: "", description: "" });
  };

  // Save module
  const handleSaveModule = async (e) => {
    if (e) e.preventDefault();
    if (!moduleForm.title.trim()) {
      toast.error("Please enter a module title");
      return;
    }
    setSavingModule(true);
    try {
      if (editingModuleId === "new") {
        await axios.post(`${API}/modules`, {
          course_id: courseId,
          title: moduleForm.title.trim(),
          description: moduleForm.description.trim(),
          order: (course?.modules_data?.length || 0) + 1
        }, { withCredentials: true });
        toast.success("Module created successfully");
      } else {
        await axios.put(`${API}/modules/${editingModuleId}`, {
          title: moduleForm.title.trim(),
          description: moduleForm.description.trim()
        }, { withCredentials: true });
        toast.success("Module updated successfully");
      }
      setEditingModuleId(null);
      setModuleForm({ title: "", description: "" });
      fetchCourseTree();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save module");
    } finally {
      setSavingModule(false);
    }
  };

  // Delete module
  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Are you sure? This will delete all lessons inside this module too!")) return;
    try {
      await axios.delete(`${API}/modules/${moduleId}`, { withCredentials: true });
      toast.success("Module deleted");
      if (editingModuleId === moduleId) setEditingModuleId(null);
      fetchCourseTree();
    } catch (err) {
      toast.error("Failed to delete module");
    }
  };

  const emptyLessonForm = {
    title: "",
    description: "",
    video_url: "",
    duration_minutes: "",
    notes_url: "",
    notes_title: "",
    notes_file_id: "",
    notes_type: "upload"
  };

  // Start editing a lesson
  const startEditLesson = (lesson) => {
    setEditingLessonId(lesson.lesson_id);
    setAddingLessonModuleId(null);
    setShowVideoPreview(false);
    setNotesSourceTab(lesson.notes_type === "link" ? "link" : "upload");
    setLessonForm({
      title: lesson.title || "",
      description: lesson.description || "",
      video_url: lesson.video_url || "",
      duration_minutes: lesson.duration_minutes || "",
      notes_url: lesson.notes_url || (lesson.resources?.[0]?.url || ""),
      notes_title: lesson.notes_title || (lesson.resources?.[0]?.name || ""),
      notes_file_id: lesson.notes_file_id || (lesson.resources?.[0]?.file_id || ""),
      notes_type: lesson.notes_type || (lesson.notes_file_id ? "upload" : "link")
    });
  };

  // Start adding a lesson
  const startAddLesson = (moduleId) => {
    setAddingLessonModuleId(moduleId);
    setEditingLessonId(null);
    setShowVideoPreview(false);
    setNotesSourceTab("upload");
    setLessonForm(emptyLessonForm);
    setCollapsedModules(prev => ({ ...prev, [moduleId]: false }));
  };

  // Upload PDF or notes from computer
  const handleNotesFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingNotes(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", "notes");
      const res = await axios.post(`${API}/uploads`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });
      setLessonForm(prev => ({
        ...prev,
        notes_url: res.data.url,
        notes_file_id: res.data.file_id,
        notes_title: prev.notes_title?.trim() || file.name,
        notes_type: "upload"
      }));
      toast.success(`Attached "${file.name}" successfully!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload PDF/notes");
    } finally {
      setUploadingNotes(false);
      // Reset input value so re-selecting same file works
      e.target.value = "";
    }
  };

  // Save lesson
  const handleSaveLesson = async (e, moduleId) => {
    if (e) e.preventDefault();
    if (!lessonForm.title.trim()) {
      toast.error("Please enter a lesson title");
      return;
    }
    setSavingLesson(true);
    try {
      const resourcesList = lessonForm.notes_url?.trim() ? [{
        name: lessonForm.notes_title?.trim() || "Lesson Study Notes",
        url: lessonForm.notes_url.trim(),
        file_id: lessonForm.notes_file_id || "",
        type: lessonForm.notes_type || "link"
      }] : [];

      if (addingLessonModuleId) {
        const targetModule = course.modules_data.find(m => m.module_id === addingLessonModuleId);
        await axios.post(`${API}/lessons`, {
          module_id: addingLessonModuleId,
          title: lessonForm.title.trim(),
          description: lessonForm.description.trim() || "",
          video_url: lessonForm.video_url.trim() || "",
          duration_minutes: parseInt(lessonForm.duration_minutes) || 0,
          notes_url: lessonForm.notes_url?.trim() || "",
          notes_title: lessonForm.notes_title?.trim() || "",
          notes_file_id: lessonForm.notes_file_id || "",
          notes_type: lessonForm.notes_type || "link",
          resources: resourcesList,
          order: (targetModule?.lessons?.length || 0) + 1
        }, { withCredentials: true });
        toast.success("Lesson added successfully with notes");
      } else if (editingLessonId) {
        await axios.put(`${API}/lessons/${editingLessonId}`, {
          title: lessonForm.title.trim(),
          description: lessonForm.description.trim(),
          video_url: lessonForm.video_url.trim(),
          duration_minutes: parseInt(lessonForm.duration_minutes) || 0,
          notes_url: lessonForm.notes_url?.trim() || "",
          notes_title: lessonForm.notes_title?.trim() || "",
          notes_file_id: lessonForm.notes_file_id || "",
          notes_type: lessonForm.notes_type || "link",
          resources: resourcesList
        }, { withCredentials: true });
        toast.success("Lesson updated successfully");
      }
      setEditingLessonId(null);
      setAddingLessonModuleId(null);
      setShowVideoPreview(false);
      setLessonForm(emptyLessonForm);
      fetchCourseTree();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save lesson");
    } finally {
      setSavingLesson(false);
    }
  };

  // Delete lesson
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Are you sure you want to delete this lesson?")) return;
    try {
      await axios.delete(`${API}/lessons/${lessonId}`, { withCredentials: true });
      toast.success("Lesson deleted");
      if (editingLessonId === lessonId) setEditingLessonId(null);
      fetchCourseTree();
    } catch (err) {
      toast.error("Failed to delete lesson");
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-xs text-gray-400 dark:text-zinc-400 mt-4 font-mono tracking-wide">Loading course curriculum...</p>
      </div>
    );
  }

  if (!course) return null;

  const totalLessons = (course.modules_data || []).reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
  const currentVideoInfo = getVideoEmbedInfo(lessonForm.video_url);

  // Quick duration presets in minutes
  const durationPresets = [15, 30, 45, 60, 90, 120];

  const renderNotesAttachmentSection = () => {
    const hasNotes = Boolean(lessonForm.notes_url?.trim());

    return (
      <div className="space-y-3 p-3.5 sm:p-4 rounded-xl bg-slate-50/70 dark:bg-white/[0.02] border border-slate-200 dark:border-white/[0.08]">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <Label className="text-xs text-slate-800 dark:text-white font-semibold flex items-center gap-1.5">
                <span>Study Notes / Slides PDF</span>
              </Label>
              <p className="text-[11px] text-slate-500 dark:text-zinc-400">
                Attach slides or notes for students to read inside the course player
              </p>
            </div>
          </div>
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 font-mono text-[10px] uppercase font-bold px-2 py-0.5 flex items-center gap-1 border">
            <Lock className="w-3 h-3" />
            <span>Protected View-Only (No Download)</span>
          </Badge>
        </div>

        {/* Tab switch between Upload from Computer and Paste Link */}
        <div className="flex items-center gap-1 p-1 bg-slate-200/70 dark:bg-black/40 border border-slate-300 dark:border-white/[0.08] rounded-lg max-w-sm">
          <button
            type="button"
            onClick={() => setNotesSourceTab("upload")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-semibold transition-all ${
              notesSourceTab === "upload"
                ? "bg-emerald-600 text-white shadow-2xs"
                : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Upload from Computer</span>
          </button>
          <button
            type="button"
            onClick={() => setNotesSourceTab("link")}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-xs font-semibold transition-all ${
              notesSourceTab === "link"
                ? "bg-emerald-600 text-white shadow-2xs"
                : "text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <Link2 className="w-3.5 h-3.5" />
            <span>Paste URL / Drive Link</span>
          </button>
        </div>

        {/* Content based on tab */}
        {notesSourceTab === "upload" ? (
          <div className="space-y-2.5">
            <div className="relative">
              <input
                type="file"
                id="notes-file-input"
                accept=".pdf,.doc,.docx,application/pdf"
                onChange={handleNotesFileUpload}
                disabled={uploadingNotes}
                className="hidden"
              />
              <label
                htmlFor="notes-file-input"
                className={`flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                  uploadingNotes
                    ? "border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-500/5 cursor-wait"
                    : hasNotes && lessonForm.notes_type === "upload"
                    ? "border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-500/5 hover:bg-emerald-50"
                    : "border-slate-300 dark:border-white/15 bg-white dark:bg-black/30 hover:border-emerald-500 hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                }`}
              >
                {uploadingNotes ? (
                  <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                    <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    <span>Uploading and securing document...</span>
                  </div>
                ) : hasNotes && lessonForm.notes_type === "upload" ? (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2.5">
                      <FileCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <div>
                        <p className="text-xs text-slate-900 dark:text-white font-semibold">{lessonForm.notes_title || "Attached PDF"}</p>
                        <p className="text-[10px] text-slate-500 dark:text-zinc-400 font-mono">Stored securely in database • Click to replace file</p>
                      </div>
                    </div>
                    <span className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline font-semibold">Replace File</span>
                  </div>
                ) : (
                  <div className="text-center py-1">
                    <Upload className="w-6 h-6 text-slate-400 dark:text-zinc-400 mx-auto mb-1.5" />
                    <p className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Click to select PDF or notes from computer</p>
                    <p className="text-[10px] text-slate-500 dark:text-zinc-500 mt-0.5">Supports PDF documents (max 50MB) • Embedded read-only for students</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <Label className="text-[11px] text-slate-700 dark:text-zinc-400 font-medium">PDF / Document Web Link</Label>
              <Input
                type="url"
                placeholder="https://drive.google.com/file/d/... OR https://example.com/notes.pdf"
                value={lessonForm.notes_url}
                onChange={(e) => setLessonForm({
                  ...lessonForm,
                  notes_url: e.target.value,
                  notes_type: "link",
                  notes_title: lessonForm.notes_title || "Lesson Study Material"
                })}
                className="mt-1 h-9 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs font-mono rounded-xl shadow-2xs"
              />
            </div>
            <p className="text-[11px] text-slate-500 dark:text-zinc-500">
              💡 For Google Drive files, ensure sharing is set to "Anyone with the link can view". It will automatically open in protected preview mode.
            </p>
          </div>
        )}

        {/* Notes Title & Removal if an attachment exists */}
        {hasNotes && (
          <div className="pt-2 border-t border-slate-200 dark:border-white/[0.06] flex items-center gap-2">
            <div className="flex-1">
              <Label className="text-[11px] text-slate-700 dark:text-zinc-400 font-medium">Document Display Title</Label>
              <Input
                type="text"
                placeholder="e.g. Demand & Supply Zone Cheatsheet"
                value={lessonForm.notes_title}
                onChange={(e) => setLessonForm({ ...lessonForm, notes_title: e.target.value })}
                className="mt-1 h-8 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-lg shadow-2xs"
              />
            </div>
            <button
              type="button"
              onClick={() => setLessonForm({
                ...lessonForm,
                notes_url: "",
                notes_title: "",
                notes_file_id: "",
                notes_type: "upload"
              })}
              className="h-8 mt-4 px-3 rounded-lg text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 dark:hover:bg-rose-500/20 border border-rose-200 dark:border-rose-500/30 transition-all flex items-center shadow-2xs"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Remove
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300" data-testid="course-builder">
      {/* ── Top Bar / Studio Header ── */}
      <div className="relative overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 sm:p-7 shadow-xs">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <button 
              type="button"
              onClick={onBack} 
              className="inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-zinc-300 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors mb-3 font-mono font-semibold group bg-slate-100 hover:bg-emerald-50 dark:bg-white/[0.04] dark:hover:bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-emerald-300 dark:border-white/[0.08] dark:hover:border-emerald-500/30 shadow-2xs"
            >
              <span className="group-hover:-translate-x-1 transition-transform">&larr;</span> Back to Course Manager
            </button>

            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-outfit font-extrabold text-slate-900 dark:text-white tracking-tight">
                  {course.title}
                </h1>
                <p className="text-xs text-slate-500 dark:text-zinc-400 font-plex mt-0.5 line-clamp-1">
                  Curriculum Architecture &amp; Video Session Manager
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-4 text-xs font-mono text-slate-700 dark:text-zinc-400">
              <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-black/40 px-3 py-1 rounded-lg border border-slate-200 dark:border-white/[0.06]">
                <Layers className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                <span className="text-slate-900 dark:text-white font-bold">{course.modules_data?.length || 0}</span> Modules
              </div>
              <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-black/40 px-3 py-1 rounded-lg border border-slate-200 dark:border-white/[0.06]">
                <PlayCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-slate-900 dark:text-white font-bold">{totalLessons}</span> Lessons
              </div>
              {course.slug && (
                <div className="hidden sm:flex items-center gap-1 text-[11px] text-slate-500 dark:text-zinc-500 font-mono">
                  Slug: <span className="text-slate-700 dark:text-zinc-300 font-semibold">/courses/{course.slug}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button 
              onClick={startAddModule} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5 rounded-xl shadow-xs transition-all"
              data-testid="add-module-btn"
            >
              <Plus className="w-4 h-4 mr-2 stroke-[2.5]" /> Add Module
            </Button>
          </div>
        </div>
      </div>

      {/* ── Inline "Add New Module" Card ── */}
      {editingModuleId === "new" && (
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-white dark:bg-slate-900/95 p-5 sm:p-7 shadow-md animate-in fade-in slide-in-from-top-3 duration-200">
          <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 dark:border-white/[0.08] mb-5">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <h3 className="font-outfit font-bold text-slate-900 dark:text-white text-base sm:text-lg">Create New Course Module</h3>
            </div>
            <button 
              type="button" 
              onClick={() => setEditingModuleId(null)}
              className="text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={handleSaveModule} className="space-y-4">
            <div>
              <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Module Title *</Label>
              <Input 
                required 
                autoFocus
                placeholder="e.g. Identification of High-Probability Zones"
                value={moduleForm.title} 
                onChange={e => setModuleForm({ ...moduleForm, title: e.target.value })} 
                className="mt-1.5 h-11 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.12] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-slate-900 dark:text-white rounded-xl text-sm shadow-2xs"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Description (Optional)</Label>
              <Textarea 
                placeholder="Brief summary of what this module covers..."
                value={moduleForm.description} 
                onChange={e => setModuleForm({ ...moduleForm, description: e.target.value })} 
                className="mt-1.5 min-h-[75px] bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.12] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-slate-900 dark:text-white rounded-xl text-sm resize-none shadow-2xs"
              />
            </div>
            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button 
                type="button" 
                onClick={() => setEditingModuleId(null)} 
                className="border border-slate-300 dark:border-white/10 text-slate-700 dark:text-zinc-200 bg-white hover:bg-slate-100 dark:bg-white/5 dark:hover:bg-white/10 h-9 rounded-xl px-4 text-xs font-semibold transition-all shadow-2xs"
              >
                Cancel
              </button>
              <Button 
                type="submit" 
                disabled={savingModule}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 rounded-xl px-5 text-xs shadow-xs"
              >
                {savingModule ? "Creating..." : "Create Module"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modules List ── */}
      <div className="space-y-5">
        {course.modules_data.length === 0 && editingModuleId !== "new" ? (
          <div className="text-center py-20 bg-slate-50 dark:bg-slate-900/60 border border-dashed border-slate-300 dark:border-white/[0.1] rounded-2xl p-8">
            <BookOpen className="w-12 h-12 text-slate-400 dark:text-zinc-500 mx-auto mb-3 stroke-[1.5]" />
            <h3 className="font-outfit font-bold text-slate-900 dark:text-white text-lg">No curriculum modules created yet</h3>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1.5 max-w-md mx-auto">
              Start structuring your course by adding your first module with lessons and private video sessions.
            </p>
            <Button onClick={startAddModule} className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 px-5 text-xs rounded-xl shadow-xs">
              <Plus className="w-4 h-4 mr-1.5" /> Add First Module
            </Button>
          </div>
        ) : (
          course.modules_data.map((mod, index) => {
            const isEditingThisModule = editingModuleId === mod.module_id;
            const isCollapsed = !!collapsedModules[mod.module_id];
            const isAddingLessonHere = addingLessonModuleId === mod.module_id;

            return (
              <div 
                key={mod.module_id} 
                className={`rounded-2xl transition-all duration-200 overflow-hidden border ${
                  isEditingThisModule 
                    ? "border-emerald-500/60 bg-white dark:bg-slate-900 shadow-md ring-1 ring-emerald-500/30" 
                    : "border-slate-200 dark:border-white/[0.08] bg-white dark:bg-slate-900/90 hover:border-slate-300 dark:hover:border-white/[0.16] shadow-2xs"
                }`}
                data-testid={`module-card-${mod.module_id}`}
              >
                {/* ── MODULE HEADER ── */}
                {isEditingThisModule ? (
                  /* ── INLINE MODULE EDIT FORM ── */
                  <div className="p-5 sm:p-7 bg-white dark:bg-slate-900/95 border-b border-slate-200 dark:border-slate-800 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 dark:border-white/[0.08] mb-4">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 font-mono font-bold text-[10px] uppercase px-2.5 py-0.5 border">
                          Module {index + 1}
                        </Badge>
                        <span className="text-xs font-mono text-slate-800 dark:text-zinc-200 font-semibold flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Quick Edit Module
                        </span>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => setEditingModuleId(null)}
                        className="text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <form onSubmit={handleSaveModule} className="space-y-4">
                      <div>
                        <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Module Title *</Label>
                        <Input 
                          required 
                          autoFocus
                          value={moduleForm.title} 
                          onChange={e => setModuleForm({ ...moduleForm, title: e.target.value })} 
                          className="mt-1.5 h-11 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-slate-900 dark:text-white font-outfit font-bold text-sm sm:text-base rounded-xl shadow-2xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Description (Optional)</Label>
                        <Textarea 
                          value={moduleForm.description} 
                          onChange={e => setModuleForm({ ...moduleForm, description: e.target.value })} 
                          placeholder="Module learning objectives..."
                          className="mt-1.5 min-h-[65px] bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-slate-900 dark:text-white text-xs sm:text-sm rounded-xl resize-none shadow-2xs"
                        />
                      </div>
                      <div className="flex items-center justify-end gap-2.5 pt-2">
                        <button 
                          type="button" 
                          onClick={() => setEditingModuleId(null)} 
                          className="border border-slate-300 dark:border-white/10 text-slate-700 dark:text-zinc-200 bg-white hover:bg-slate-100 dark:bg-white/5 dark:hover:bg-white/10 h-9 rounded-xl px-4 text-xs font-semibold transition-all shadow-2xs"
                        >
                          Cancel
                        </button>
                        <Button 
                          type="submit" 
                          disabled={savingModule}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 rounded-xl px-5 text-xs shadow-xs flex items-center gap-1.5"
                        >
                          <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                          {savingModule ? "Saving..." : "Save Module"}
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : (
                  /* ── STANDARD MODULE HEADER ── */
                  <div className="p-4 sm:p-5 flex items-start justify-between gap-3 bg-slate-50/70 dark:bg-white/[0.02] border-b border-slate-100 dark:border-white/[0.04]">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 font-mono text-[10px] uppercase font-bold px-2.5 py-0.5 border">
                          Module {index + 1}
                        </Badge>
                        <h3 className="text-base sm:text-lg font-outfit font-bold text-slate-900 dark:text-white tracking-tight truncate">
                          {mod.title}
                        </h3>
                      </div>
                      {mod.description && (
                        <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-400 mt-1 line-clamp-2">
                          {mod.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-slate-500 dark:text-zinc-500">
                        <span className="flex items-center gap-1 text-slate-600 dark:text-zinc-400 font-medium">
                          <PlayCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> {mod.lessons?.length || 0} Lessons
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* EDIT MODULE BUTTON */}
                      <button 
                        type="button"
                        onClick={() => startEditModule(mod)} 
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-emerald-50 dark:bg-white/5 dark:hover:bg-emerald-500/10 text-slate-700 hover:text-emerald-700 dark:text-zinc-200 dark:hover:text-emerald-400 border border-slate-200 hover:border-emerald-300 dark:border-white/10 dark:hover:border-emerald-500/30 transition-all shadow-2xs cursor-pointer"
                        title="Edit Module Title"
                        data-testid={`edit-module-btn-${mod.module_id}`}
                      >
                        <PenSquare className="w-3.5 h-3.5 text-slate-600 dark:text-zinc-300" />
                        <span>Edit</span>
                      </button>
                      
                      {/* DELETE MODULE BUTTON */}
                      <button 
                        type="button"
                        onClick={() => handleDeleteModule(mod.module_id)} 
                        className="h-8 w-8 rounded-lg flex items-center justify-center bg-slate-100 hover:bg-rose-50 dark:bg-white/5 dark:hover:bg-rose-500/10 text-slate-600 hover:text-rose-600 dark:text-zinc-400 dark:hover:text-rose-400 border border-slate-200 hover:border-rose-300 dark:border-white/10 dark:hover:border-rose-500/30 transition-all shadow-2xs cursor-pointer"
                        title="Delete Module"
                        data-testid={`delete-module-btn-${mod.module_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>

                      {/* COLLAPSE/EXPAND BUTTON */}
                      <button 
                        type="button"
                        onClick={() => toggleCollapse(mod.module_id)} 
                        className="h-8 w-8 rounded-lg flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 hover:text-slate-900 dark:text-zinc-300 dark:hover:text-white border border-slate-200 dark:border-white/10 transition-all shadow-2xs cursor-pointer"
                        title={isCollapsed ? "Expand Lessons" : "Collapse Lessons"}
                      >
                        {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── LESSONS LIST ── */}
                {!isCollapsed && (
                  <div className="p-3 sm:p-5 bg-slate-50/60 dark:bg-black/40 border-t border-slate-100 dark:border-white/[0.06] space-y-3">
                    {/* Render existing lessons */}
                    {mod.lessons && mod.lessons.map((lesson, lIndex) => {
                      const isEditingThisLesson = editingLessonId === lesson.lesson_id;
                      const lessonVideo = getVideoEmbedInfo(lesson.video_url);

                      if (isEditingThisLesson) {
                        return (
                          /* ── INLINE LESSON EDIT FORM ── */
                          <div 
                            key={lesson.lesson_id} 
                            className="p-4 sm:p-6 rounded-2xl border-2 border-emerald-500/40 bg-white dark:bg-slate-900/95 shadow-md space-y-4 animate-in fade-in duration-200"
                          >
                            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/[0.08]">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-xs font-mono font-bold text-emerald-700 dark:text-emerald-400 uppercase flex items-center gap-1.5 tracking-wider">
                                  <PlayCircle className="w-3.5 h-3.5" /> Edit Lesson {lIndex + 1}
                                </span>
                              </div>
                              <button 
                                type="button" 
                                onClick={() => setEditingLessonId(null)}
                                className="text-slate-400 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-white p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            <form onSubmit={(e) => handleSaveLesson(e, mod.module_id)} className="space-y-4">
                              <div>
                                <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Lesson Title *</Label>
                                <Input 
                                  required 
                                  autoFocus
                                  value={lessonForm.title} 
                                  onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} 
                                  className="mt-1.5 h-10 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-sm rounded-xl shadow-2xs"
                                />
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                  <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold flex items-center gap-2">
                                    <span>Video URL (Google Drive / YouTube / Vimeo / Direct MP4)</span>
                                  </Label>
                                  {/* Provider detection pill */}
                                  {currentVideoInfo.isValid && (
                                    <Badge className={`${currentVideoInfo.badgeClass} border font-mono text-[10px] uppercase font-bold px-2 py-0.5 flex items-center gap-1`}>
                                      {currentVideoInfo.isDrive && <HardDrive className="w-3 h-3" />}
                                      {currentVideoInfo.provider === 'youtube' && <PlayCircle className="w-3 h-3" />}
                                      {currentVideoInfo.provider === 'vimeo' && <Video className="w-3 h-3" />}
                                      <span>{currentVideoInfo.label}</span>
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  <Input 
                                    type="url"
                                    placeholder="https://drive.google.com/file/d/.../view OR https://youtu.be/..."
                                    value={lessonForm.video_url} 
                                    onChange={e => setLessonForm({ ...lessonForm, video_url: e.target.value })} 
                                    className="h-10 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl font-mono flex-1 shadow-2xs"
                                  />
                                  {currentVideoInfo.isValid && (
                                    <button
                                      type="button"
                                      onClick={() => setShowVideoPreview(!showVideoPreview)}
                                      className={`h-10 px-3 rounded-xl border text-xs font-mono font-semibold transition-all shadow-2xs flex items-center gap-1.5 ${
                                        showVideoPreview 
                                          ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/40" 
                                          : "bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-white/[0.04] dark:text-zinc-300 dark:hover:text-white border-slate-200 dark:border-white/[0.1]"
                                      }`}
                                    >
                                      {showVideoPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                      <span>{showVideoPreview ? "Hide Preview" : "Test Play"}</span>
                                    </button>
                                  )}
                                </div>

                                {/* Google Drive helper hint */}
                                <div className="p-2.5 rounded-lg bg-emerald-50/50 dark:bg-white/[0.02] border border-emerald-200 dark:border-white/[0.05] text-[11px] text-slate-700 dark:text-zinc-400 flex items-start gap-2">
                                  <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                                  <div>
                                    <span className="text-emerald-700 dark:text-emerald-400 font-semibold font-mono">Private Video Hosting:</span> You can paste any private Google Drive link! Just ensure Google Drive share access is set to <em>"Anyone with the link can view"</em>. It embeds cleanly in the player without YouTube ads or recommendations.
                                  </div>
                                </div>

                                {/* Live Video Preview Player */}
                                {showVideoPreview && currentVideoInfo.isValid && (
                                  <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 bg-black aspect-video relative my-3 shadow-2xl animate-in zoom-in-95 duration-200">
                                    {currentVideoInfo.provider === 'direct' ? (
                                      <video src={currentVideoInfo.embedUrl} controls className="w-full h-full object-contain" />
                                    ) : (
                                      <iframe
                                        src={currentVideoInfo.embedUrl}
                                        title="Lesson Video Preview"
                                        className="w-full h-full border-0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                      />
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Duration (Minutes)</Label>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-slate-500 dark:text-zinc-500 font-mono mr-1">Quick:</span>
                                    {durationPresets.map(preset => (
                                      <button
                                        key={preset}
                                        type="button"
                                        onClick={() => setLessonForm({ ...lessonForm, duration_minutes: String(preset) })}
                                        className={`px-2 py-0.5 rounded text-[10px] font-mono transition-all ${
                                          Number(lessonForm.duration_minutes) === preset
                                            ? "bg-emerald-600 text-white font-semibold shadow-2xs"
                                            : "bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-white/[0.04] dark:hover:bg-white/[0.08] dark:text-zinc-400 dark:hover:text-white border border-slate-200 dark:border-transparent"
                                        }`}
                                      >
                                        {preset}m
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <Input 
                                  type="number"
                                  min="0"
                                  placeholder="e.g. 45"
                                  value={lessonForm.duration_minutes} 
                                  onChange={e => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })} 
                                  className="h-10 bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl shadow-2xs"
                                />
                              </div>

                              <div>
                                <Label className="text-xs text-slate-800 dark:text-zinc-200 font-semibold">Description (Optional)</Label>
                                <Textarea 
                                  value={lessonForm.description} 
                                  onChange={e => setLessonForm({ ...lessonForm, description: e.target.value })} 
                                  placeholder="Lesson summary, reference notes, or links..."
                                  className="mt-1.5 min-h-[60px] bg-white dark:bg-black/60 border border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl resize-none shadow-2xs"
                                />
                              </div>

                              {renderNotesAttachmentSection()}

                              <div className="flex items-center justify-end gap-2.5 pt-2">
                                <button 
                                  type="button" 
                                  onClick={() => setEditingLessonId(null)} 
                                  className="border border-slate-300 dark:border-white/10 text-slate-700 dark:text-zinc-200 bg-white hover:bg-slate-100 dark:bg-white/5 dark:hover:bg-white/10 h-9 text-xs px-4 rounded-xl font-semibold transition-all shadow-2xs"
                                >
                                  Cancel
                                </button>
                                <Button 
                                  type="submit" 
                                  disabled={savingLesson}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 text-xs px-5 rounded-xl shadow-xs"
                                >
                                  {savingLesson ? "Saving..." : "Save Lesson"}
                                </Button>
                              </div>
                            </form>
                          </div>
                        );
                      }

                      return (
                        /* ── STANDARD LESSON ROW ── */
                        <div 
                          key={lesson.lesson_id} 
                          className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 dark:bg-white/[0.03] hover:bg-slate-100/90 dark:hover:bg-white/[0.06] border border-slate-200 dark:border-white/[0.08] rounded-xl transition-all group shadow-2xs"
                          data-testid={`lesson-row-${lesson.lesson_id}`}
                        >
                          <div className="flex items-center gap-3.5 min-w-0">
                            <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-black/60 border border-slate-300 dark:border-white/10 flex items-center justify-center text-xs font-mono font-bold text-slate-700 dark:text-zinc-300 shrink-0 shadow-2xs">
                              {lIndex + 1}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs sm:text-sm text-slate-900 dark:text-white font-semibold truncate">
                                {lesson.title}
                              </p>
                              <div className="flex items-center gap-2.5 mt-1 text-[11px] font-mono flex-wrap">
                                {lessonVideo.isValid ? (
                                  <span className={`inline-flex items-center gap-1 font-medium ${lessonVideo.color}`}>
                                    {lessonVideo.isDrive ? <HardDrive className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />}
                                    <span>{lessonVideo.isDrive ? "Google Drive" : lessonVideo.label}</span>
                                  </span>
                                ) : (
                                  <span className="text-slate-400 dark:text-zinc-500">No Video</span>
                                )}

                                {lesson.duration_minutes > 0 && (
                                  <>
                                    <span className="text-slate-300 dark:text-zinc-600">•</span>
                                    <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300 font-medium">
                                      <Clock className="w-3 h-3" /> {lesson.duration_minutes} min
                                    </span>
                                  </>
                                )}

                                {(lesson.notes_url || (lesson.resources && lesson.resources.length > 0)) && (
                                  <>
                                    <span className="text-slate-300 dark:text-zinc-600">•</span>
                                    <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                      <FileText className="w-3 h-3" />
                                      <span className="truncate max-w-[140px]">{lesson.notes_title || lesson.resources?.[0]?.name || "PDF Notes"}</span>
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button 
                              type="button"
                              onClick={() => startEditLesson(lesson)} 
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 border border-slate-200 hover:border-emerald-300 dark:bg-white/5 dark:hover:bg-emerald-950/40 dark:text-zinc-200 dark:hover:text-emerald-300 dark:border-white/10 transition-colors shadow-2xs cursor-pointer"
                              title="Edit Lesson"
                            >
                              <PenSquare className="w-3.5 h-3.5" />
                              <span>Edit</span>
                            </button>
                            <button 
                              type="button"
                              onClick={() => handleDeleteLesson(lesson.lesson_id)} 
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-500 hover:text-rose-600 bg-slate-100 hover:bg-rose-50 border border-slate-200 hover:border-rose-200 dark:bg-white/5 dark:hover:bg-rose-950/40 dark:text-zinc-400 dark:hover:text-rose-400 dark:border-white/10 transition-colors shadow-2xs cursor-pointer"
                              title="Delete Lesson"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* ── INLINE "ADD LESSON" FORM ── */}
                    {isAddingLessonHere ? (
                      <div className="p-4 sm:p-6 rounded-2xl border-2 border-emerald-500/30 bg-white dark:bg-slate-900/95 shadow-md space-y-4 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-white/[0.08]">
                          <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 uppercase flex items-center gap-2 tracking-wider">
                            <Plus className="w-4 h-4 stroke-[2.5]" /> New Lesson for Module {index + 1}
                          </span>
                          <button 
                            type="button" 
                            onClick={() => setAddingLessonModuleId(null)}
                            className="text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-white p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <form onSubmit={(e) => handleSaveLesson(e, mod.module_id)} className="space-y-4">
                          <div>
                            <Label className="text-xs font-semibold text-slate-700 dark:text-zinc-200">Lesson Title *</Label>
                            <Input 
                              required 
                              autoFocus
                              placeholder="e.g. Identifying Demand Zones on 15m Timeframe"
                              value={lessonForm.title} 
                              onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} 
                              className="mt-1.5 h-10 bg-white dark:bg-slate-950 border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-sm rounded-xl font-medium"
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <Label className="text-xs font-semibold text-slate-700 dark:text-zinc-200">
                                Video URL (Google Drive / YouTube / Vimeo / Direct MP4)
                              </Label>
                              {currentVideoInfo.isValid && (
                                <Badge className={`${currentVideoInfo.badgeClass} border font-mono text-[10px] uppercase font-bold px-2 py-0.5 flex items-center gap-1`}>
                                  {currentVideoInfo.isDrive && <HardDrive className="w-3 h-3" />}
                                  {currentVideoInfo.provider === 'youtube' && <PlayCircle className="w-3 h-3" />}
                                  {currentVideoInfo.provider === 'vimeo' && <Video className="w-3 h-3" />}
                                  <span>{currentVideoInfo.label}</span>
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              <Input 
                                type="url"
                                placeholder="https://drive.google.com/file/d/.../view OR https://youtu.be/..."
                                value={lessonForm.video_url} 
                                onChange={e => setLessonForm({ ...lessonForm, video_url: e.target.value })} 
                                className="h-10 bg-white dark:bg-slate-950 border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl font-mono flex-1"
                              />
                              {currentVideoInfo.isValid && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => setShowVideoPreview(!showVideoPreview)}
                                  className={`h-10 px-3 rounded-xl border text-xs font-mono font-medium transition-all ${
                                    showVideoPreview 
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/40" 
                                      : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300 dark:bg-white/[0.04] dark:text-zinc-300 dark:hover:text-white dark:border-white/[0.1]"
                                  }`}
                                >
                                  {showVideoPreview ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                                  {showVideoPreview ? "Hide Preview" : "Test Play"}
                                </Button>
                              )}
                            </div>

                            {/* Google Drive hint */}
                            <div className="p-3 rounded-xl bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-900 dark:text-emerald-300 flex items-start gap-2.5">
                              <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                              <div className="leading-relaxed">
                                <span className="font-bold font-mono">Private Video Hosting:</span> You can paste any private Google Drive link! Just ensure Google Drive share access is set to <em>"Anyone with the link can view"</em>. It embeds cleanly in the player without YouTube ads or recommendations.
                              </div>
                            </div>

                            {/* Live Video Preview Player */}
                            {showVideoPreview && currentVideoInfo.isValid && (
                              <div className="rounded-xl overflow-hidden border border-slate-300 dark:border-white/10 bg-black aspect-video relative my-3 shadow-2xl animate-in zoom-in-95 duration-200">
                                {currentVideoInfo.provider === 'direct' ? (
                                  <video src={currentVideoInfo.embedUrl} controls className="w-full h-full object-contain" />
                                ) : (
                                  <iframe
                                    src={currentVideoInfo.embedUrl}
                                    title="Lesson Video Preview"
                                    className="w-full h-full border-0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                  />
                                )}
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-semibold text-slate-700 dark:text-zinc-200">Duration (Minutes)</Label>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-slate-500 dark:text-zinc-500 font-mono mr-1">Quick:</span>
                                {durationPresets.map(preset => (
                                  <button
                                    key={preset}
                                    type="button"
                                    onClick={() => setLessonForm({ ...lessonForm, duration_minutes: String(preset) })}
                                    className={`px-2 py-0.5 rounded text-[10px] font-mono transition-all font-semibold ${
                                      Number(lessonForm.duration_minutes) === preset
                                        ? "bg-emerald-600 text-white font-bold shadow-2xs"
                                        : "bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-white/[0.04] dark:hover:bg-white/[0.08] dark:text-zinc-400 dark:hover:text-white border border-slate-200 dark:border-transparent"
                                    }`}
                                  >
                                    {preset}m
                                  </button>
                                ))}
                              </div>
                            </div>
                            <Input 
                              type="number"
                              min="0"
                              placeholder="e.g. 45"
                              value={lessonForm.duration_minutes} 
                              onChange={e => setLessonForm({ ...lessonForm, duration_minutes: e.target.value })} 
                              className="h-10 bg-white dark:bg-slate-950 border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl"
                            />
                          </div>

                          <div>
                            <Label className="text-xs font-semibold text-slate-700 dark:text-zinc-200">Description (Optional)</Label>
                            <Textarea 
                              value={lessonForm.description} 
                              onChange={e => setLessonForm({ ...lessonForm, description: e.target.value })} 
                              placeholder="Key takeaways, resources, or chart screenshots..."
                              className="mt-1.5 min-h-[60px] bg-white dark:bg-slate-950 border-slate-300 dark:border-white/[0.15] focus:border-emerald-500 text-slate-900 dark:text-white text-xs rounded-xl resize-none"
                            />
                          </div>

                          {renderNotesAttachmentSection()}

                          <div className="flex items-center justify-end gap-2.5 pt-2">
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={() => setAddingLessonModuleId(null)} 
                              className="border-slate-300 dark:border-white/10 text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-white/10 h-9 text-xs px-4 rounded-xl font-semibold"
                            >
                              Cancel
                            </Button>
                            <Button 
                              type="submit" 
                              disabled={savingLesson}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 text-xs px-5 rounded-xl shadow-xs"
                            >
                              {savingLesson ? "Adding..." : "Add Lesson"}
                            </Button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      <button 
                        type="button" 
                        onClick={() => startAddLesson(mod.module_id)}
                        className="w-full mt-2.5 border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 bg-slate-50/80 hover:bg-emerald-50/60 dark:bg-white/[0.02] dark:hover:bg-emerald-950/20 text-slate-600 hover:text-emerald-700 dark:text-zinc-400 dark:hover:text-emerald-300 h-10 text-xs font-semibold rounded-xl transition-all shadow-2xs flex items-center justify-center gap-2 cursor-pointer"
                        data-testid={`add-lesson-btn-${mod.module_id}`}
                      >
                        <Plus className="w-4 h-4 stroke-[2.5]" />
                        <span>Add Lesson to Module</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
